import { EmoBriefcase, EmoNeutral } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { Heart, Plus } from "lucide-react"
import { useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import { jobPath, jobsNewPath, jobsPath } from "@/routes"

interface JobItem {
  id: string | number
  source_id?: number
  type: "job"
  created_at: string
  title: string
  company_name: string
  location_type?: string
  category?: string
  excerpt?: string
  source?: string
  source_name?: string
  is_favorited?: boolean
  favorites_count?: number
}

interface Props {
  jobs: JobItem[]
  filters?: {
    search?: string
    sort?: string
    category?: string
    location_type?: string
    source?: string
    status?: string
  }
  can_create: boolean
  pagination?: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

const LOCATION_LABELS: Record<string, string> = {
  remote: "Remoto",
  hybrid: "Híbrido",
  onsite: "Presencial",
}

const CATEGORY_LABELS: Record<string, string> = {
  tech: "Tecnología",
  gastronomia: "Gastronomía",
  servicios: "Servicios",
  administracion: "Administración",
  ventas: "Ventas",
  educacion: "Educación",
  salud: "Salud",
  construccion: "Construcción",
  limpieza: "Limpieza",
  transporte: "Transporte",
}

function JobCard({ job }: { job: JobItem }) {
  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  const jobId = job.source_id ?? Number(job.id)
  const storedFavorite = getStoreFavorite("job", jobId)
  const storedCount = getStoreCount("job", jobId)

  const [localFavorited, setLocalFavorited] = useState(
    storedFavorite ?? Boolean(job.is_favorited),
  )
  const [localFavoritesCount, setLocalFavoritesCount] = useState(
    storedCount ?? job.favorites_count ?? 0,
  )

  useEffect(() => {
    const currentStoreFavorite = getStoreFavorite("job", jobId)
    const currentStoreCount = getStoreCount("job", jobId)
    setLocalFavorited(currentStoreFavorite ?? Boolean(job.is_favorited))
    setLocalFavoritesCount(currentStoreCount ?? job.favorites_count ?? 0)
  }, [
    jobId,
    job.is_favorited,
    job.favorites_count,
    getStoreFavorite,
    getStoreCount,
  ])

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const newValue = !localFavorited
    const newCount = newValue
      ? localFavoritesCount + 1
      : Math.max(0, localFavoritesCount - 1)

    setLocalFavorited(newValue)
    setLocalFavoritesCount(newCount)
    setStoreFavorite("job", jobId, newValue)
    setStoreCount("job", jobId, newCount)

    router.post(
      `/curros/${jobId}/favorite`,
      {},
      { preserveScroll: true, preserveState: true },
    )
  }

  return (
    <div className="group bg-card relative block overflow-hidden rounded-[10px] p-4 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <Link href={jobPath(job.id)} className="block">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-[12px] font-medium tracking-[0.18px]">
              {job.company_name}
            </p>
            <h3 className="text-foreground group-hover:text-primary line-clamp-2 text-[14px] font-semibold tracking-[0.21px] transition-colors">
              {job.title}
            </h3>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {job.location_type && (
            <Badge
              variant="secondary"
              className="text-[10px] font-semibold tracking-[0.57px]"
            >
              {(
                LOCATION_LABELS[job.location_type] ?? job.location_type
              ).toUpperCase()}
            </Badge>
          )}
          {job.category && (
            <Badge
              variant="outline"
              className="text-muted-foreground text-[10px] tracking-[0.57px]"
            >
              {CATEGORY_LABELS[job.category] ?? job.category}
            </Badge>
          )}
          {job.source === "external" && job.source_name && (
            <Badge
              variant="outline"
              className="text-muted-foreground text-[10px] tracking-[0.57px]"
            >
              {job.source_name}
            </Badge>
          )}
        </div>
        {job.excerpt && (
          <p className="text-muted-foreground mt-2 line-clamp-2 text-[12px] tracking-[0.18px]">
            {job.excerpt}
          </p>
        )}
      </Link>
      <AuthButton
        variant="ghost"
        size="sm"
        className="bg-card/80 hover:bg-card absolute top-2 right-2 h-auto rounded-full p-1"
        onClick={handleFavorite}
      >
        <Heart
          className={`h-4 w-4 ${localFavorited ? "text-destructive fill-current" : "text-muted-foreground"}`}
        />
        {localFavoritesCount > 0 && (
          <span className="ml-1 text-xs">{localFavoritesCount}</span>
        )}
      </AuthButton>
    </div>
  )
}

export default function JobsIndex({
  jobs = [],
  filters = {},
  can_create,
  pagination,
}: Props) {
  const currentSort = filters.sort ?? "recent"
  const [allJobs, setAllJobs] = useState(jobs)
  const [isLoading, setIsLoading] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)

  useEffect(() => {
    if (isRequesting) return

    if (jobs.length > 0) {
      setAllJobs((prev) => {
        const urlParams = new URLSearchParams(window.location.search)
        const currentOffset = parseInt(urlParams.get("offset") ?? "0")
        const isLoadMore = currentOffset > 0

        if (!isLoadMore) {
          return jobs
        } else {
          const newJobs = jobs.filter(
            (newJob) => !prev.some((existing) => existing.id === newJob.id),
          )
          return [...prev, ...newJobs]
        }
      })
      setIsLoading(false)
    } else if (jobs.length === 0 && !isLoading) {
      setAllJobs([])
    }
  }, [jobs, isLoading, isRequesting])

  const doNavigate = (
    params: Record<string, string | number | boolean | null | undefined>,
  ) => {
    if (isRequesting) return

    setIsRequesting(true)
    setIsLoading(true)
    router.get(jobsPath(), params, {
      preserveScroll: true,
      preserveState: true,
      only: ["jobs", "filters", "can_create", "pagination"],
      onFinish: () => {
        setIsRequesting(false)
        setIsLoading(false)
      },
    })
  }

  const handleFilterChange = (key: string, value: string) => {
    setAllJobs([])
    doNavigate({
      ...filters,
      [key]: value === "" || value === "all" ? undefined : value,
      offset: 0,
    })
  }

  const handleLoadMore = () => {
    if (isLoading || !pagination?.has_more || isRequesting) return

    setIsLoading(true)
    setIsRequesting(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      jobsPath(),
      { ...filters, offset: nextOffset },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["jobs", "pagination"],
        onFinish: () => {
          setIsLoading(false)
          setIsRequesting(false)
        },
      },
    )
  }

  return (
    <AppLayout>
      <Head title="Curros" />

      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-6xl space-y-3 md:p-6">
          <div className="flex flex-col">
            <div className="flex items-center justify-center gap-4">
              <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
                <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                  <img
                    src={EmoBriefcase}
                    alt="Curros"
                    className="h-full w-full object-contain"
                  />
                </span>
                Curros
              </h1>
              {can_create && (
                <Button
                  variant="default"
                  onClick={() => router.visit(jobsNewPath())}
                  className="flex flex-shrink-0 items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Publicar curro</span>
                </Button>
              )}
            </div>
            <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
              Ofertas de trabajo para la comunidad.
            </p>
          </div>

          {/* Sort tabs */}
          <Tabs
            value={currentSort}
            onValueChange={(v) => handleFilterChange("sort", v)}
          >
            <div className="flex justify-center px-1">
              <TabsList>
                <TabsTrigger value="recent" disabled={isRequesting}>
                  Recientes
                </TabsTrigger>
                <TabsTrigger value="most_viewed" disabled={isRequesting}>
                  Más Vistas
                </TabsTrigger>
              </TabsList>
            </div>
          </Tabs>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 px-3">
            <Select
              value={filters.category ?? "all"}
              onValueChange={(v) => handleFilterChange("category", v)}
            >
              <SelectTrigger className="h-9 w-auto min-w-[130px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.location_type ?? "all"}
              onValueChange={(v) => handleFilterChange("location_type", v)}
            >
              <SelectTrigger className="h-9 w-auto min-w-[120px]">
                <SelectValue placeholder="Modalidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las modalidades</SelectItem>
                {Object.entries(LOCATION_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Content area */}
          <div className="mt-6 space-y-6">
            {allJobs.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 px-3 pb-2 sm:grid-cols-2">
                {allJobs.map((job: JobItem) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            ) : (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>
                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No se encontraron curros
                </h3>
              </div>
            )}

            {allJobs.length > 0 && pagination?.has_more && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="text-primary hover:bg-muted"
                >
                  {isLoading ? "Cargando..." : "Ver más curros"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
